<div class="landing-root">
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <!-- HEADER BAR -->
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <header class="landing-header">
    <div class="header-inner">
      <!-- Logo + Platform Name -->
      <div class="header-brand" (click)="scrollToProducts()">
        <img src="images/logo/docnexus-icon.svg" alt="DocNexus" class="header-logo" />
        <span class="header-title">DocNexus</span>
      </div>

      <!-- Desktop Nav — right-aligned -->
      <nav class="header-nav">
        <!-- Products with dropdown -->
        <div class="nav-dropdown-wrapper"
             (mouseenter)="productsDropdownOpen = true"
             (mouseleave)="productsDropdownOpen = false">
          <a class="nav-link" (click)="scrollToProducts()">
            Products
            <mat-icon class="nav-chevron" [class.chevron-open]="productsDropdownOpen">expand_more</mat-icon>
          </a>
          @if (productsDropdownOpen) {
            <div class="nav-dropdown">
              <div class="nav-dropdown-panel">
              @for (product of products; track product.id) {
                <a class="dropdown-item" (click)="scrollToProduct(product.id)">
                  @if (product.svgIcon) {
                    <img [src]="product.svgIcon" class="dropdown-icon svg-icon" [alt]="product.name" />
                  } @else {
                    <mat-icon class="dropdown-icon" [style.color]="product.accentColor">{{ product.icon }}</mat-icon>
                  }
                  <div class="dropdown-text">
                    <span class="dropdown-name">{{ product.name }}</span>
                    <span class="dropdown-tagline">{{ product.tagline }}</span>
                  </div>
                  @if (!product.ready) {
                    <span class="dropdown-badge">Soon</span>
                  }
                </a>
              }
              </div>
            </div>
          }
        </div>

        <a class="nav-link nav-disabled">Pricing</a>
        <a class="nav-link nav-disabled">Contact</a>
        <button class="header-login" (click)="navigateToLogin()">Login</button>
      </nav>

      <!-- Mobile Hamburger -->
      <button class="mobile-menu-btn" (click)="toggleMobileMenu()">
        <mat-icon>{{ mobileMenuOpen ? 'close' : 'menu' }}</mat-icon>
      </button>
    </div>

    <!-- Mobile Nav Dropdown -->
    @if (mobileMenuOpen) {
      <div class="mobile-nav">
        <a class="mobile-nav-link" (click)="scrollToProducts(); toggleMobileMenu()">Products</a>
        <div class="mobile-products-list">
          @for (product of products; track product.id) {
            <a class="mobile-product-link" (click)="scrollToProduct(product.id)">
              @if (product.svgIcon) {
                <img [src]="product.svgIcon" class="svg-icon" [style.width]="'20px'" [style.height]="'20px'" [alt]="product.name" />
              } @else {
                <mat-icon [style.color]="product.accentColor">{{ product.icon }}</mat-icon>
              }
              <span>{{ product.name }}</span>
            </a>
          }
        </div>
        <a class="mobile-nav-link nav-disabled">Pricing</a>
        <a class="mobile-nav-link nav-disabled">Contact</a>
        <button class="mobile-login-btn" (click)="navigateToLogin()">Login</button>
      </div>
    }
  </header>

  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <!-- HERO SECTION -->
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <section class="hero-section">
    <div class="hero-content">
      <h1 class="hero-title">
        The Nexus of<br />Document Intelligence
      </h1>
      <p class="hero-subtitle">
        AI-powered tools for document hyperlinking, translation,
        formatting, and comparison &mdash; built for regulatory professionals.
      </p>
      <button class="hero-cta" (click)="scrollToProducts()">
        Explore Products
        <mat-icon class="cta-arrow">arrow_downward</mat-icon>
      </button>
    </div>

    <!-- Decorative gradient orbs -->
    <div class="hero-orb hero-orb-1"></div>
    <div class="hero-orb hero-orb-2"></div>
    <div class="hero-orb hero-orb-3"></div>
  </section>

  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <!-- PRODUCT ROWS — Alternating layout -->
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <section id="products-section" class="products-section">
    @for (product of products; track product.id; let i = $index) {
      <div
        class="product-row"
        [id]="'product-' + product.id"
        [class.row-reversed]="i % 2 !== 0"
        [style.--product-accent]="product.accentColor"
        [style.--product-accent-light]="product.accentLight"
      >
        <div class="product-row-inner">
        <!-- Graphic Side -->
        <div class="product-graphic">
          <div class="graphic-container">
            <img [src]="product.illustration" [alt]="product.name + ' illustration'" class="product-illustration" />
          </div>
        </div>

        <!-- Content Side -->
        <div class="product-content">
          <div class="product-content-inner">
            @if (!product.ready) {
              <span class="coming-soon-badge">Coming Soon</span>
            }
            <h2 class="product-name">
              @if (product.svgIcon) {
                <img [src]="product.svgIcon" class="product-name-icon svg-icon" [alt]="product.name" />
              } @else {
                <mat-icon class="product-name-icon">{{ product.icon }}</mat-icon>
              }
              {{ product.name }}
            </h2>
            <h3 class="product-tagline">{{ product.tagline }}</h3>
            <p class="product-description">{{ product.description }}</p>

            <ul class="product-features">
              @for (feature of product.features; track feature) {
                <li class="feature-item">
                  <mat-icon class="feature-check">check_circle</mat-icon>
                  <span>{{ feature }}</span>
                </li>
              }
            </ul>

            <button
              class="product-cta"
              [class.cta-disabled]="!product.ready"
              (click)="onProductClick(product)"
            >
              {{ product.ready ? 'Get Started' : 'Coming Soon' }}
              @if (product.ready) {
                <mat-icon class="cta-arrow-right">arrow_forward</mat-icon>
              }
            </button>
          </div>
        </div>
        </div>
      </div>

      <!-- Section nav chevron (between products, not after the last) -->
      @if (i < products.length - 1) {
        <div class="section-nav-chevron" (click)="scrollToNextProduct(i)">
          <div class="chevron-circle">
            <mat-icon>keyboard_arrow_down</mat-icon>
          </div>
        </div>
      }
    }
  </section>

  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <!-- FOOTER -->
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <footer class="landing-footer">
    <div class="footer-inner">
      <div class="footer-brand">
        <img src="images/logo/docnexus-icon.svg" alt="DocNexus" class="footer-logo" />
        <span class="footer-title">DocNexus</span>
      </div>

      <div class="footer-trust">
        <div class="trust-badge">
          <mat-icon class="trust-icon">verified_user</mat-icon>
          <span>Enterprise SSO</span>
        </div>
        <div class="trust-badge">
          <mat-icon class="trust-icon">lock</mat-icon>
          <span>Data Encryption</span>
        </div>
        <div class="trust-badge">
          <mat-icon class="trust-icon">history</mat-icon>
          <span>Audit Logging</span>
        </div>
        <div class="trust-badge">
          <mat-icon class="trust-icon">speed</mat-icon>
          <span>99.9% Uptime</span>
        </div>
      </div>

      <p class="footer-copy">&copy; 2026 DocNexus. All rights reserved.</p>
    </div>
  </footer>
</div>
